import { useEffect, useMemo } from 'react';
import { useField } from 'formik';
import SuitCaseIcon from 'components/icons/SuitCaseIcon';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import useJobs from 'hooks/useJobs';
import useSession from 'hooks/useSession';
import { checkIsClearanceAgent } from 'config/roles/roles';
import debounce from 'lodash.debounce';
import { GetJobsParams, JobResponse } from 'store/jobs/job';
import { useBrokerUploadContext } from '../BrokerUploadProvider';
import { BrokerUploadSteps } from '../types';
import Button from 'components/new-ui/base/button/Button';
import { useLocation, useNavigate } from 'react-router-dom';

const stepKey = BrokerUploadSteps.JOB;
const fieldName = 'job.jobId';

const JobStep = () => {
    const [fieldProps, fieldMeta, fieldHelper] = useField(fieldName);
    const { isActive, isComplete, isIdle, resetStep, stepsConfig } = useMultiStepFormContext<any>();
    const { selectedJob, setSelectedJob } = useBrokerUploadContext();
    const { userInfo } = useSession();
    const isClearanceAgent = checkIsClearanceAgent(userInfo);
    const { jobs, listJobs, isLoading } = useJobs();
    const navigate = useNavigate();
    const location = useLocation();

    const isPreSelected = stepsConfig.find((s) => s.key === stepKey)?.disabled ?? false;

    useEffect(() => {
        const params: GetJobsParams = { size: 50 };
        if (isClearanceAgent) params.ownerId = userInfo?.individualId;
        listJobs(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const debouncedSearch = debounce((query: string) => {
        const params: GetJobsParams = query ? { query, size: 50 } : { size: 50 };
        if (isClearanceAgent) params.ownerId = userInfo?.individualId;
        listJobs(params);
    }, 500);

    // Add the selected job to the options so we don't show the UUID for a small period of time
    const jobOptions = useMemo(() => {
        const options = (jobs.list ?? []).map((job: JobResponse) => ({
            id: job.id,
            value: job.referenceId,
        }));

        if (selectedJob && !options.find((o) => o.id === selectedJob.id)) {
            options.unshift({ id: selectedJob.id, value: selectedJob.referenceId });
        }

        return options;
    }, [jobs.list, selectedJob]);

    const sectionTitle = 'Job';

    if (isIdle(stepKey)) {
        return <FormSectionHeader title={sectionTitle} icon={<SuitCaseIcon />} />;
    }

    if (isComplete(stepKey)) {
        const canReset = isComplete(stepKey) && !isPreSelected;
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <FormSectionHeader title={sectionTitle} icon={<SuitCaseIcon />} />
                <ReadonlyFormItem
                    disabled={isPreSelected}
                    value={selectedJob?.referenceId}
                    onReset={
                        canReset
                            ? () => {
                                  setSelectedJob(undefined);
                                  resetStep(stepKey, 'jobId');
                              }
                            : undefined
                    }
                />
            </div>
        );
    }

    if (isActive(stepKey)) {
        return (
            <div>
                <FormSectionHeader
                    title={sectionTitle}
                    icon={<SuitCaseIcon />}
                    button={
                        // Pass the current location so we can be navigated back here after the job has been created
                        <Button onClick={() => navigate('/jobs/new', { state: { from: location.pathname } })}>
                            Create Job
                        </Button>
                    }
                />
                <FormSelect
                    style={{ marginTop: 16, maxWidth: '100%' }}
                    placeholder="Search Job"
                    selectOptions={jobOptions}
                    hideKeys
                    fieldProps={fieldProps}
                    fieldMeta={fieldMeta}
                    fieldHelper={fieldHelper}
                    loading={isLoading}
                    filterOption={false}
                    onSearch={(q) => debouncedSearch(q)}
                    onChange={(jobId: string) => {
                        const job = (jobs.list ?? []).find((j: JobResponse) => j.id === jobId);
                        if (job) setSelectedJob(job);
                    }}
                />
            </div>
        );
    }

    return null;
};

export default JobStep;
